import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

interface TextWidgetConfig {
  text?: string;
  content?: string;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: string | number;
  letterSpacing?: string | number;
  wrap?: boolean;
  maxLines?: number;
  textOverflow?: 'clip' | 'ellipsis';
  font?: {
    size?: string | number;
    weight?: string | number;
    style?: 'normal' | 'italic' | 'oblique';
    family?: string;
  };
}

@Component({
  selector: 'app-text-widget',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './text-widget.component.html',
  styleUrl: './text-widget.component.scss',
})
export class TextWidgetComponent {
  @Input() data?: TextWidgetConfig;

  get text(): string {
    return String(this.data?.text ?? this.data?.content ?? '');
  }

  get fontSize(): string {
    const size = this.data?.font?.size;
    return this.withDefaultUnit(size, '14px');
  }

  get fontWeight(): string {
    const weight = this.data?.font?.weight;
    return String(weight ?? 400);
  }

  get fontStyle(): string {
    return this.data?.font?.style || 'normal';
  }

  get fontFamily(): string {
    return this.data?.font?.family || 'inherit';
  }

  get lineHeight(): string {
    return this.withDefaultUnit(this.data?.lineHeight, '1.4');
  }

  get letterSpacing(): string {
    return this.withDefaultUnit(this.data?.letterSpacing, 'normal');
  }

  get color(): string {
    return this.data?.color || 'var(--dvm-content-text, #111827)';
  }

  get textAlign(): string {
    return this.data?.align || 'left';
  }

  get wrapEnabled(): boolean {
    return this.data?.wrap !== false;
  }

  get maxLines(): number | null {
    const lines = Number(this.data?.maxLines);
    return Number.isFinite(lines) && lines > 0 ? lines : null;
  }

  get overflowMode(): string {
    return this.data?.textOverflow === 'clip' ? 'clip' : 'ellipsis';
  }

  get shouldClamp(): boolean {
    return this.maxLines !== null;
  }

  private withDefaultUnit(value: string | number | undefined, fallback: string): string {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }
    if (typeof value === 'number') {
      return `${value}px`;
    }
    return String(value);
  }
}
